import React, { useEffect, useState } from 'react';
import styles from './AddOrEditTutorial.module.scss';
import { useForm } from 'react-hook-form';
/* eslint-disable no-useless-escape */
import {
  Divider,
  TextField,
  styled,
  Select,
  MenuItem,
  useMediaQuery,
  Typography,
  useTheme,
} from '@mui/material';
import { openNotification, Button } from 'tt-ui-lib/core';
import { ReloadIcon, LitterBinIcon, AddIcon } from 'tt-ui-lib/icons';
import { useDropzone } from 'react-dropzone';
import LinearProgress from '@mui/material/LinearProgress/LinearProgress';
import { v1 as uuidv1 } from 'uuid';
import axios from 'axios';
import { useLazyQuery, useMutation } from '@apollo/client';
import {
  GET_URL_FOR_PUBLIC_UPLOAD,
  CREATE_TUTORIAL,
  GET_TUTORIAL_BY_ID,
  UPDATE_TUTORIAL,
  GET_SECTIONS,
} from '../../../../../../../../api';
import { DropZone } from '../../../../../../../../components/dropZone';
import PartialLoader from '../../../../../../../../components/layout/PartialLoader';

export const MuiTextField = styled(TextField)(({ theme }) => ({
  '& .MuiInputBase-root': {
    height: '48px',
    borderRadius: '12px',
  },
  '& .MuiInputBase-input': {
    height: '48px',
    padding: '0 14px',
  },
  ".MuiInputLabel-root[data-shrink='false']": {
    transform: 'translate(14px, 13px) scale(1) !important',
  },
}));

const AddOrEditTutorial = ({
  tutorialActionOrId,
  activeSection,
  setTutorialAction,
  tutorialsCount,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down(901));
  const [fileToS3, setFileToS3] = useState(null);
  const [sections, setSections] = useState([]);
  const [positions, setPositions] = useState(
    Array.from({ length: tutorialsCount }, (_, index) => index + 1)
  );
  const [previewFile, setPreviewFile] = useState({});
  const [coverFile, setCoverFile] = useState({});
  const [file, setFile] = useState({});
  const [fileUrl, setFileUrl] = useState('');
  const [addLink, setAddLink] = useState(false);
  const [pageLoading, setPageLoading] = useState(false);
  const [getPublicUrl] = useMutation(GET_URL_FOR_PUBLIC_UPLOAD, {
    variables: { input: { fileToS3 } },
  });
  const [getTutorialById] = useLazyQuery(GET_TUTORIAL_BY_ID);
  const [createNewTutorial] = useMutation(CREATE_TUTORIAL);
  const [updateTutorial] = useMutation(UPDATE_TUTORIAL);
  const [getSections] = useLazyQuery(GET_SECTIONS);
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm();

  useEffect(() => {
    if (fileUrl || watch('link')) {
      clearErrors('file');
    }
    if (previewFile.fullName) {
      clearErrors('preview');
    }
  }, [fileUrl, watch('link'), previewFile]);

  useEffect(() => {
    (async () => {
      setPageLoading(true);
      const sectionsRes = await getSections({
        variables: {
          type: 'tutorial',
        },
        fetchPolicy: 'network-only',
      });

      if (sectionsRes && sectionsRes.data && sectionsRes.data.showAllSections) {
        setSections(sectionsRes.data.showAllSections);
      }

      if (tutorialActionOrId && tutorialActionOrId !== 'add') {
        const response = await getTutorialById({
          variables: { id: tutorialActionOrId },
          fetchPolicy: 'network-only',
        });
        if (response.data && response.data.showTutorialById) {
          const data = response.data.showTutorialById;
          setValue('name', data.name);
          setValue('description', data.description);
          setValue('section_id', data.section_id);
          setValue('position', data.position);
          setValue('keywords', data.keywords);
          setValue('link', data.link);
          setPreviewFile({ image: data.preview, fullName: data.preview.split('/').pop() });
          setFile({
            url: data.file,
            name: data.file_name.split('.').slice(-2).join('.'),
            extension: data.type,
            fullName: data.file_name,
          });
          setFileUrl(data.file);
          setAddLink(true);
        }
      }
      if (tutorialActionOrId === 'add') {
        if (tutorialsCount !== 0) {
          const lastItem = positions.at(-1) + 1;
          setPositions((current) => [...current, lastItem]);
          setValue('position', lastItem);
        } else {
          setPositions([1]);
        }
      }
      setPageLoading(false);
    })();
  }, [tutorialActionOrId]);

  const convertFileSize = (bytes) => {
    const KB = 1024;
    const MB = KB * 1024;

    if (bytes >= MB) {
      return `${(bytes / MB).toFixed(2)} MB`;
    }
    return `${(bytes / KB).toFixed(2)} KB`;
  };

  const uploadFile = async (fileValue, type) => {
    try {
      const formatFile = fileValue?.name.substr(-4, 4);
      let filesName;
      if (formatFile === '.') {
        filesName = `${uuidv1()}.${fileValue?.name}${formatFile}`;
      } else {
        filesName = `${uuidv1()}.${fileValue?.name}`;
      }
      const path = `tutorials/${type}`;
      const toUpload = { path: path, name: filesName };
      setFileToS3(toUpload);
      const { data } = await getPublicUrl({ variables: { input: toUpload } });
      setFileToS3(null);
      const urlToUpload = data?.getUrlForPublicUpload?.url;
      if (type === 'files') {
        const urlToView = `https://${data?.getUrlForPublicUpload?.download_url}`;
        setFileUrl(urlToView);
        setFile((prev) => ({ ...prev, fullName: filesName }));
      }
      if (type === 'preview') {
        setPreviewFile((prev) => ({ ...prev, fullName: filesName }));
      }
      await axios
        .put(urlToUpload, fileValue, {
          headers: {
            'Content-type': fileValue.type,
            'Access-Control-Allow-Origin': '*',
          },
          onUploadProgress: (progressEvent) => {
            if (type === 'preview') {
              const percentCompleted = Math.round(
                (progressEvent.loaded * 100) / progressEvent.total
              );
              setPreviewFile((current) => ({ ...current, progress: percentCompleted }));
            }
          },
        })
        .catch((err) => {
          if (type === 'preview') {
            setPreviewFile((current) => ({ ...current, error: true, progress: 0 }));
          }
          console.log('Error!', err);
        });
    } catch (error) {
      console.log('error !!!', error);
      openNotification({
        message: 'Error while uploading file.',
        type: 'error',
      });
    }
  };

  const onDropped = (files, type) => {
    if (files && files.length) {
      for (let i = 0; i < files.length; i++) {
        const reader = new FileReader();
        if (type === 'preview') {
          reader.onload = (e) => {
            setPreviewFile({
              image: e.target.result,
              progress: 0,
              error: false,
              file: files[i],
            });
          };
          uploadFile(files[i], type);
        }
        if (type === 'cover') {
          reader.onload = (e) => {
            setCoverFile({
              image: e.target.result,
              progress: 100,
              error: false,
              file: files[i],
            });
          };
        }
        if (type === 'files') {
          reader.onload = (e) => {
            const fileName = files[i].name;
            const fileExtension = fileName.split('.').pop();
            setFile({
              name: fileName,
              base64: e.target.result,
              url: fileUrl,
              extension: fileExtension,
              size: convertFileSize(files[i].size),
            });
          };
          uploadFile(files[i], type);
        }
        reader.readAsDataURL(files[i]);
      }
    }
  };

  const {
    acceptedFiles: previewAcceptedFiles,
    getRootProps: getPreviewRootProps,
    getInputProps: getPreviewInputProps,
  } = useDropzone({
    accept: {
      'image/jpeg': ['.jpeg', '.jpg'],
      'image/png': ['.png'],
      'image/gif': ['.gif'],
    },
    multiple: false,
    onDrop: (files) => onDropped(files, 'preview'),
  });

  const {
    acceptedFiles: coverAcceptedFiles,
    getRootProps: getCoverRootProps,
    getInputProps: getCoverInputProps,
  } = useDropzone({
    accept: {
      'image/jpeg': ['.jpeg', '.jpg'],
      'image/png': ['.png'],
      'image/gif': ['.gif'],
    },
    multiple: false,
    onDrop: (files) => onDropped(files, 'cover'),
  });

  function getFileType(fileToCheck) {
    const mimeType = fileToCheck.base64.match(/^data:(.*?);base64,/)[1];

    if (mimeType.startsWith('video/')) {
      return 'video';
    }
    if (mimeType.startsWith('image/')) {
      return 'image';
    }
    if (mimeType === 'application/pdf') {
      return 'pdf';
    }
    return 'other';
  }

  const onSubmit = async (data) => {
    if (!fileUrl && !watch('link')) {
      openNotification({
        message: 'You must feel all required fields',
        type: 'error',
      });
      setError('file', { type: 'custom', message: 'Add file or link' });
      return;
    }
    if (!previewFile.fullName) {
      setError('preview', { type: 'custom', message: 'Add preview photo' });
      return;
    }
    if (tutorialActionOrId && tutorialActionOrId !== 'add') {
      const updateInput = {
        ...data,
        id: tutorialActionOrId,
        preview: previewFile.fullName || '',
        file: file.fullName || '',
      };
      if (file && file.base64) {
        updateInput.type = getFileType(file);
      } else if (data.link) {
        updateInput.type = 'link';
      }
      const response = await updateTutorial({ variables: { input: updateInput } });
      if (response && response.data && response.data.updateTutorial) {
        openNotification({
          message: 'Tutorial updated successfully',
          type: 'success',
        });
        setTutorialAction('');
      }
    } else {
      // ToDo add size on inputData and save in backend
      const input = {
        ...data,
        preview: previewFile.fullName || '',
        file: file.fullName || '',
      };
      if (file && file.base64) {
        input.type = getFileType(file);
      } else if (data.link) {
        input.type = 'link';
      }
      const response = await createNewTutorial({ variables: { input: input } });
      if (response && response.data && response.data.createTutorial) {
        openNotification({
          message: 'Tutorial created successfully',
          type: 'success',
        });
        setTutorialAction('');
      }
    }
  };

  const openPdfFile = () => {
    const newWindow = window.open(fileUrl, '_blank', 'noopener,noreferrer');
    if (newWindow) newWindow.opener = null;
  };

  const deleteFile = (e) => {
    e.stopPropagation();
    setFile({});
    setFileUrl('');
  };

  return (
    <>
      {pageLoading ? (
        <PartialLoader loading={pageLoading} isLightContainer />
      ) : (
        <form onSubmit={handleSubmit(onSubmit)} className={styles.pageContent}>
          <div className={styles.form}>
            <div className={styles.inputsBlock}>
              <MuiTextField
                label="Document name"
                {...register('name', {
                  required: 'Please fill out the rest fields. This is mandatory.',
                  pattern: {
                    value: /^[\s\S]{1,255}$/,
                    message: "You can't write more than 255 characters",
                  },
                })}
                error={!!errors.name}
                helperText={errors.name?.message}
              />

              <TextField
                {...register('description', {
                  pattern: {
                    value: /^[\s\S]{1,3000}$/,
                    message: "You can't write more than 3000 characters",
                  },
                })}
                error={!!errors.description}
                helperText={errors.description?.message}
                className={styles.textarea}
                label="Description"
                multiline
                rows={4}
                inputProps={{ style: { resize: 'vertical' } }}
              />
              <Select
                {...register('section_id')}
                size="medium"
                className={styles.select}
                sx={{
                  '.MuiOutlinedInput-notchedOutline': { border: '1px solid lightgrey' },
                  '.MuiSelect-select': { padding: 0 },
                }}
                displayEmpty
                value={watch('section_id')}
                defaultValue={activeSection?.id}
                renderValue={(selected) => {
                  const selectedItem = sections.find((item) => item.id === selected);
                  return selectedItem ? selectedItem.name : 'Select section';
                }}
              >
                <MenuItem disabled>Select section </MenuItem>
                {sections.map((item) => (
                  <MenuItem value={item.id} key={item.id}>
                    {item.name}
                  </MenuItem>
                ))}
              </Select>
              <Select
                {...register('position', {
                  required: 'Please fill out the rest fields. This is mandatory.',
                })}
                size="medium"
                className={styles.select}
                sx={{
                  '.MuiOutlinedInput-notchedOutline': { border: '1px solid lightgrey' },
                  '.MuiSelect-select': { padding: 0 },
                }}
                error={!!errors.position}
                displayEmpty
                onChange={(event) => {
                  const selectedValue = event.target.value;
                  if (selectedValue) {
                    clearErrors('position');
                  }
                  setValue('position', selectedValue);
                }}
                value={watch('position')}
                renderValue={(selected) => {
                  if (!selected) {
                    return <p>Position in the block</p>;
                  }
                  return selected;
                }}
              >
                <MenuItem disabled>Position in the block</MenuItem>
                {positions.map((item) => (
                  <MenuItem value={item} key={item}>
                    {item}
                  </MenuItem>
                ))}
              </Select>
              {errors.position?.message && (
                <Typography className={styles.errorMessage}>{errors.position?.message}</Typography>
              )}
              <TextField
                {...register('keywords', {
                  pattern: {
                    value: /^[\s\S]{1,3000}$/,
                    message: "You can't write more than 3000 characters",
                  },
                })}
                className={styles.textarea}
                label="Keywords"
                multiline
                rows={4}
                inputProps={{ style: { resize: 'vertical' } }}
                error={!!errors.keywords}
                helperText={errors.keywords?.message}
              />
            </div>
            <Divider
              orientation={!isMobile ? 'vertical' : 'horizontal'}
              aria-hidden="true"
              flexItem
            />
            <div className={styles.inputsBlock}>
              <div className={styles.titleWithBtn}>
                <div className={styles.title}>Preview</div>
                <div className={styles.addDocBtn} {...getPreviewRootProps()}>
                  <input {...getPreviewInputProps()} />
                  <AddIcon style={{ width: 24, height: 24 }} />
                  <div>Add File</div>
                </div>
              </div>
              {errors.preview?.message && (
                <Typography className={styles.errorMessage}>{errors.preview?.message}</Typography>
              )}
              {previewFile.image && (
                <div
                  key={previewFile.image}
                  className={styles.image}
                  style={{
                    border: `1.8px solid ${previewFile.error ? '#D32F2F' : 'transparent'}`,
                  }}
                >
                  <img src={previewFile.image} alt="" />
                  {previewFile.progress < 100 && (
                    <div className={styles.imageLoader}>
                      {previewFile.error ? (
                        <div className={styles.reloadBtn}>
                          <ReloadIcon width={24} height={24} />
                        </div>
                      ) : (
                        <LinearProgress
                          className={styles.lineProgress}
                          value={previewFile.progress}
                          variant="determinate"
                        />
                      )}
                    </div>
                  )}
                  <div className={styles.deleteIcon} onClick={() => setPreviewFile({})}>
                    <LitterBinIcon style={{ width: 24, height: 24 }} />
                  </div>
                </div>
              )}
              <div className={styles.titleWithBtn}>
                <div className={styles.title}>Cover</div>
                <div className={styles.addDocBtn} {...getCoverRootProps()}>
                  <input {...getCoverInputProps()} />
                  <AddIcon style={{ width: 24, height: 24 }} />
                  <div>Add File</div>
                </div>
              </div>
              {coverFile.image && (
                <div
                  key={coverFile.image}
                  className={styles.image}
                  style={{
                    border: `1.8px solid ${coverFile.error ? '#D32F2F' : 'transparent'}`,
                  }}
                >
                  <img src={coverFile.image} alt="" />
                  {coverFile.progress < 100 && (
                    <div className={styles.imageLoader}>
                      {coverFile.error ? (
                        <div className={styles.reloadBtn}>
                          <ReloadIcon width={24} height={24} />
                        </div>
                      ) : (
                        <LinearProgress
                          className={styles.lineProgress}
                          value={coverFile.progress}
                          variant="determinate"
                        />
                      )}
                    </div>
                  )}
                  <div className={styles.deleteIcon}>
                    <LitterBinIcon style={{ width: 24, height: 24 }} />
                  </div>
                </div>
              )}

              <div className={styles.titleWithBtn}>
                <div className={styles.title}>File</div>
                <div className={styles.addDocBtn} onClick={() => setAddLink(true)}>
                  <AddIcon style={{ width: 24, height: 24 }} />
                  <div>Add File/Link</div>
                </div>
              </div>
              {addLink && (
                <div className={styles.linkAndFileBlock}>
                  <MuiTextField
                    label="Add link"
                    {...register('link', {
                      pattern: {
                        value:
                          /^(https?:\/\/)?([a-zA-Z0-9.-]+)(\.[a-zA-Z]{2,})(\/[a-zA-Z0-9#-._~:\/?@!$&'()*+,;=]*)?$/,
                        message: 'Invalid url',
                      },
                    })}
                    error={!!errors.link}
                    helperText={errors.link?.message}
                  />
                  {!fileUrl && <DropZone onDropped={onDropped} page="tutorial" />}
                </div>
              )}
              {file && fileUrl && (
                <div onClick={() => openPdfFile()} className={styles.pdfFile}>
                  <Typography className={styles.fileName}>{file.name}</Typography>
                  <Typography className={styles.fileSize}>{file.size}</Typography>
                  <Typography className={styles.fileExtension}>{file.extension}</Typography>
                  <Button type="icon" onClick={deleteFile}>
                    <LitterBinIcon style={{ width: 24, height: 24 }} />
                  </Button>
                </div>
              )}
              {errors.file?.message && (
                <Typography className={styles.errorMessage}>{errors.file?.message}</Typography>
              )}
            </div>
          </div>
          <Button type="primary" isSubmit>
            {tutorialActionOrId && tutorialActionOrId === 'add' ? 'Publish' : 'Save'}
          </Button>
        </form>
      )}
    </>
  );
};
export default AddOrEditTutorial;
